<template>
  <div class="trade-container">
    <!-- Header Section -->
    <div class="header-section">
      <v-card
        class="header-card"
        elevation="0"
        style="
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          border-radius: 16px;
        "
      >
        <v-card-text class="pa-6">
          <div class="d-flex align-center mb-4">
            <v-icon color="white" size="32" class="mr-3">$mdiChartLine</v-icon>
            <div>
              <h1 class="text-h4 font-weight-bold text-white mb-1">
                Trading Dashboard
              </h1>
              <p class="text-white mb-0" style="opacity: 0.9">
                Review and place your investment orders
              </p>
            </div>
          </div>

          <v-alert
            class="modern-alert"
            type="warning"
            variant="tonal"
            style="
              background: rgba(255, 255, 255, 0.15);
              border: 1px solid rgba(255, 255, 255, 0.3);
            "
          >
            <template v-slot:prepend>
              <v-icon color="amber">$mdiAlert</v-icon>
            </template>
            <div class="text-white">
              <strong>Important:</strong> Zerodha users can place orders
              directly. Other brokerage users should login to your respective
              platform to place these orders.
            </div>
          </v-alert>
        </v-card-text>
      </v-card>
    </div>

    <!-- Orders Section -->
    <div class="orders-section mt-6">
      <v-card
        class="orders-card"
        elevation="0"
        style="
          background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
          border: 1px solid rgba(0, 0, 0, 0.08);
          border-radius: 16px;
        "
      >
        <v-card-text class="pa-6">
          <div class="d-flex align-center justify-space-between mb-6">
            <div>
              <h2 class="text-h5 font-weight-bold text-grey-darken-3">
                Order Details
              </h2>
              <p class="text-grey-darken-1 mb-0">
                Review and modify your investment orders
              </p>
            </div>
            <v-chip
              color="primary"
              variant="tonal"
              size="large"
              style="border-radius: 8px"
            >
              <v-icon start>$mdiWallet</v-icon>
              {{ basket.length }} {{ basket.length === 1 ? "Order" : "Orders" }}
            </v-chip>
          </div>

          <!-- Order Items -->
          <div class="order-items">
            <v-card
              v-for="(item, index) in basket"
              :key="index"
              class="order-item-card mb-4"
              elevation="0"
              style="border: 1px solid rgba(0, 0, 0, 0.08); border-radius: 12px"
            >
              <v-card-text class="pa-4">
                <v-row align="center">
                  <!-- Instrument Info -->
                  <v-col cols="12" md="3">
                    <div class="instrument-info">
                      <div class="d-flex align-center mb-2">
                        <v-icon color="primary" class="mr-2"
                          >$mdiTrendingUp</v-icon
                        >
                        <h3 class="text-h6 font-weight-bold text-grey-darken-3">
                          {{ instrumentDisplay(item).split(" Current")[0] }}
                        </h3>
                      </div>
                      <v-chip
                        color="success"
                        variant="tonal"
                        size="small"
                        style="border-radius: 6px"
                      >
                        Current: ₹{{ item.reportedPrice }}
                      </v-chip>
                    </div>
                  </v-col>

                  <!-- Price Input -->
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="item.price"
                      type="number"
                      label="Target Price"
                      variant="outlined"
                      density="comfortable"
                      hide-details
                      @update:modelValue="
                        showConfirm = true;
                        showKiteBtn = false;
                      "
                      style="border-radius: 8px"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey-darken-1">$mdiCurrencyInr</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>

                  <!-- Quantity Input -->
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="item.quantity"
                      type="number"
                      label="Quantity"
                      variant="outlined"
                      density="comfortable"
                      hide-details
                      @update:modelValue="
                        showConfirm = true;
                        showKiteBtn = false;
                      "
                      style="border-radius: 8px"
                    >
                      <template v-slot:prepend-inner>
                        <v-icon color="grey-darken-1">$mdiCounter</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>

                  <!-- Amount Display -->
                  <v-col cols="12" md="3">
                    <div class="amount-display">
                      <p class="text-caption text-grey-darken-1 mb-1">
                        Total Amount
                      </p>
                      <div class="d-flex align-center">
                        <v-icon color="success" class="mr-1"
                          >$mdiCurrencyInr</v-icon
                        >
                        <span class="text-h6 font-weight-bold text-success">
                          {{
                            Math.round(
                              item.price * item.quantity
                            ).toLocaleString()
                          }}
                        </span>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <!-- Summary & Actions Section -->
    <div class="summary-section mt-6">
      <v-card
        class="summary-card"
        elevation="0"
        style="
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          border: 1px solid rgba(0, 0, 0, 0.08);
          border-radius: 16px;
        "
      >
        <v-card-text class="pa-6">
          <v-row align="center">
            <!-- Total Investment -->
            <v-col cols="12" md="6">
              <div class="total-investment">
                <p class="text-h6 text-grey-darken-1 mb-2">Total Investment</p>
                <div class="d-flex align-center">
                  <v-icon color="primary" size="28" class="mr-2"
                    >$mdiCurrencyInr</v-icon
                  >
                  <span class="text-h3 font-weight-bold text-primary">
                    {{ totalInvestment.toLocaleString() }}
                  </span>
                </div>
              </div>
            </v-col>

            <!-- Action Buttons -->
            <v-col cols="12" md="6">
              <div class="action-buttons d-flex justify-end">
                <v-btn
                  v-if="showConfirm"
                  color="primary"
                  size="large"
                  variant="elevated"
                  @click="confirm"
                  style="
                    border-radius: 12px;
                    text-transform: none;
                    font-weight: 600;
                  "
                  class="mr-3"
                >
                  <v-icon start>$mdiCheckCircle</v-icon>
                  Confirm Orders
                </v-btn>

                <div v-if="showKiteBtn" class="kite-button-container">
                  <KiteConnectButton :apiKey="yourApiKey" :basket="basket" />
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </div>

    <!-- Features Section -->
    <div class="features-section mt-6">
      <v-row>
        <v-col cols="12" md="4">
          <v-card
            class="feature-card"
            elevation="0"
            style="
              background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
              border: 1px solid rgba(0, 0, 0, 0.08);
              border-radius: 12px;
              height: 100%;
            "
          >
            <v-card-text class="pa-4 text-center">
              <v-icon color="success" size="40" class="mb-3"
                >$mdiShieldCheck</v-icon
              >
              <h3 class="text-h6 font-weight-bold text-grey-darken-3 mb-2">
                Secure Trading
              </h3>
              <p class="text-grey-darken-1">
                Your trades are protected with bank-level security
              </p>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card
            class="feature-card"
            elevation="0"
            style="
              background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
              border: 1px solid rgba(0, 0, 0, 0.08);
              border-radius: 12px;
              height: 100%;
            "
          >
            <v-card-text class="pa-4 text-center">
              <v-icon color="warning" size="40" class="mb-3"
                >$mdiLightningBolt</v-icon
              >
              <h3 class="text-h6 font-weight-bold text-grey-darken-3 mb-2">
                Instant Execution
              </h3>
              <p class="text-grey-darken-1">
                Place orders instantly with real-time market data
              </p>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card
            class="feature-card"
            elevation="0"
            style="
              background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
              border: 1px solid rgba(0, 0, 0, 0.08);
              border-radius: 12px;
              height: 100%;
            "
          >
            <v-card-text class="pa-4 text-center">
              <v-icon color="info" size="40" class="mb-3"
                >$mdiChartTimelineVariant</v-icon
              >
              <h3 class="text-h6 font-weight-bold text-grey-darken-3 mb-2">
                Smart Analysis
              </h3>
              <p class="text-grey-darken-1">
                AI-powered insights for better investment decisions
              </p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import KiteConnectButton from "./KiteConnectButton.vue";

export default {
  components: {
    KiteConnectButton,
  },
  mounted() {
    this.basket = [];
    const exchange = "NSE";
    let instrument, price, quantity, reportedPrice;
    instrument = this.$route.query.i;
    price = this.$route.query.price;
    quantity = this.$route.query.quantity;
    reportedPrice = this.$route.query.reportedPrice;
    if (!Array.isArray(instrument)) {
      this.basket.push({
        exchange,
        instrument,
        price,
        quantity,
        reportedPrice,
      });
    } else {
      for (let i = 0; i < instrument.length; i++) {
        this.basket.push({
          exchange,
          instrument: instrument[i],
          price: price[i],
          quantity: quantity[i],
          reportedPrice: reportedPrice[i],
        });
      }
    }
    console.log("kite basket", this.basket);
  },
  methods: {
    confirm() {
      this.showKiteBtn = true;
      this.showConfirm = false;
    },
    instrumentDisplay(b) {
      const instr = b.instrument;
      let display = "";
      console.log("instrDisplay", instr);
      if (instr.indexOf(":") > -1) {
        display = instr.split(":")[1];
      } else display = instr;
      display += " Current Price:" + b.reportedPrice;
      return display;
    },
  },
  computed: {
    totalInvestment() {
      const total = this.basket.reduce((acc, item) => {
        return (acc += item.price * item.quantity * 1);
      }, 0);
      return Math.round(total);
    },
  },
  data() {
    return {
      yourApiKey: "dzvr7itl7213gjnp",
      basket: [],
      exchange: "",
      instrument: "",
      price: 0,
      quantity: 0,
      showKiteBtn: false,
      showConfirm: true,
    };
  },
};
</script>

<style scoped>
/* Container Styles */
.trade-container {
  padding: 24px;
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

/* Header Section */
.header-card {
  position: relative;
  overflow: hidden;
}

.header-card::before {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 200px;
  height: 200px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  transform: translate(50%, -50%);
}

.modern-alert {
  border-radius: 12px;
  backdrop-filter: blur(10px);
}

/* Orders Section */
.orders-card {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.orders-card:hover {
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

.order-item-card {
  transition: all 0.2s ease;
  background: white;
}

.order-item-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.instrument-info {
  position: relative;
}

.amount-display {
  text-align: center;
  padding: 12px;
  background: rgba(76, 175, 80, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(76, 175, 80, 0.2);
}

/* Summary Section */
.summary-card {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.total-investment {
  padding: 16px;
  background: rgba(59, 130, 246, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(59, 130, 246, 0.2);
}

.action-buttons {
  gap: 12px;
}

.kite-button-container {
  display: flex;
  align-items: center;
}

/* Features Section */
.feature-card {
  transition: all 0.3s ease;
  cursor: pointer;
}

.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1);
}

/* Form Controls */
.v-text-field {
  transition: all 0.2s ease;
}

.v-text-field:hover {
  transform: translateY(-1px);
}

.v-text-field.v-input--focused {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

/* Button Styles */
.v-btn {
  transition: all 0.3s ease;
}

.v-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

/* Chip Styles */
.v-chip {
  font-weight: 600;
  transition: all 0.2s ease;
}

.v-chip:hover {
  transform: scale(1.02);
}

/* Responsive Design */
@media (max-width: 960px) {
  .trade-container {
    padding: 16px;
  }

  .action-buttons {
    justify-content: center !important;
    flex-direction: column;
  }

  .action-buttons .v-btn {
    width: 100%;
    margin-bottom: 12px;
  }
}

@media (max-width: 600px) {
  .trade-container {
    padding: 12px;
  }

  .header-card .v-card-text {
    padding: 20px !important;
  }

  .text-h4 {
    font-size: 1.5rem !important;
  }

  .text-h3 {
    font-size: 1.8rem !important;
  }
}

/* Animation Keyframes */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.header-section,
.orders-section,
.summary-section {
  animation: fadeInUp 0.6s ease forwards;
}

.orders-section {
  animation-delay: 0.1s;
}

.summary-section {
  animation-delay: 0.2s;
}

.features-section {
  animation-delay: 0.3s;
}
</style>
